// frontend/src/pages/shops/TimeOffRequests.js
// Owner/Admin page to review and approve/deny staff time-off requests

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import SidebarLayout from "./SidebarLayout";

const TimeOffRequests = () => {
  const navigate = useNavigate();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [processedRequests, setProcessedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState(null); // 'approve' or 'deny'
  const [notes, setNotes] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState("pending"); // 'pending' or 'history'

  // Responsive handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

  const fetchRequests = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      if (!token) {
        navigate("/login");
        return;
      }

      // Fetch pending requests
      const pendingResponse = await fetch(
        `${API_BASE_URL}/api/schedule-exceptions/pending-approvals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (pendingResponse.ok) {
        const pendingData = await pendingResponse.json();
        setPendingRequests(pendingData.pendingRequests || []);
      } else if (pendingResponse.status === 403) {
        alert(
          "Access denied - Only shop owners/admins can view time-off requests",
        );
      }

      // Fetch processed requests (approved/denied history)
      const historyResponse = await fetch(
        `${API_BASE_URL}/api/schedule-exceptions/approval-history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setProcessedRequests(historyData.processedRequests || []);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAction = (request, action) => {
    setSelectedRequest(request);
    setModalAction(action);
    setNotes("");
    setShowModal(true);
  };

  const submitAction = async () => {
    if (!selectedRequest || !modalAction) return;

    setProcessingId(selectedRequest._id);

    try {
      const token = localStorage.getItem("ogestyle_token");
      const endpoint = modalAction === "approve" ? "approve" : "deny";

      const response = await fetch(
        `${API_BASE_URL}/api/schedule-exceptions/${selectedRequest._id}/${endpoint}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ notes }),
        },
      );

      if (response.ok) {
        alert(
          `✅ Time-off request ${modalAction === "approve" ? "approved" : "denied"} successfully`,
        );
        setShowModal(false);
        setSelectedRequest(null);
        fetchRequests(); // Refresh list
      } else {
        const error = await response.json();
        alert(error.message || `Failed to ${modalAction} request`);
      }
    } catch (error) {
      alert(`Failed to ${modalAction} request`);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return `${days} day${days !== 1 ? "s" : ""}`;
  };

  if (loading) {
    return (
      <div
        className="time-off-requests-page"
        style={{ padding: "40px", textAlign: "center" }}
      >
        <div>Loading time-off requests...</div>
      </div>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="time-off-requests-page"
        style={{
          padding: isMobile ? "16px" : "24px",
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{
              fontSize: isMobile ? "20px" : "24px",
              fontWeight: "600",
              marginBottom: "8px",
            }}
          >
            ⏰ Time-Off Requests
          </h1>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            Review and approve staff time-off requests
          </p>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginBottom: "20px",
            borderBottom: "2px solid #e5e7eb",
          }}
        >
          <button
            onClick={() => setActiveTab("pending")}
            style={{
              padding: "12px 24px",
              background: "none",
              border: "none",
              borderBottom:
                activeTab === "pending" ? "3px solid #2563eb" : "none",
              color: activeTab === "pending" ? "#2563eb" : "#6b7280",
              fontWeight: activeTab === "pending" ? "600" : "400",
              fontSize: "14px",
              cursor: "pointer",
              marginBottom: "-2px",
            }}
          >
            📋 Pending ({pendingRequests.length})
          </button>
          <button
            onClick={() => setActiveTab("history")}
            style={{
              padding: "12px 24px",
              background: "none",
              border: "none",
              borderBottom:
                activeTab === "history" ? "3px solid #2563eb" : "none",
              color: activeTab === "history" ? "#2563eb" : "#6b7280",
              fontWeight: activeTab === "history" ? "600" : "400",
              fontSize: "14px",
              cursor: "pointer",
              marginBottom: "-2px",
            }}
          >
            📚 History ({processedRequests.length})
          </button>
        </div>

        {/* Pending Requests Table */}
        {activeTab === "pending" && (
          <>
            {pendingRequests.length === 0 ? (
              <div
                style={{
                  background: "#f8f9fa",
                  padding: "40px",
                  borderRadius: "8px",
                  textAlign: "center",
                  color: "#6b7280",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  No Pending Requests
                </div>
                <div style={{ fontSize: "14px" }}>
                  All time-off requests have been reviewed
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "white",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f8f9fa",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Professional
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Start
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        End
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Duration
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Reason
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingRequests.map((request) => (
                      <tr
                        key={request._id}
                        style={{ borderBottom: "1px solid #e5e7eb" }}
                      >
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          <div style={{ fontWeight: "500" }}>
                            {request.staffId?.userId?.firstName || "N/A"}{" "}
                            {request.staffId?.userId?.lastName || ""}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Professional
                          </div>
                        </td>
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          {formatDate(request.startDate)}
                        </td>
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          {formatDate(request.endDate)}
                        </td>
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          {getDuration(request.startDate, request.endDate)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "14px",
                            textTransform: "capitalize",
                          }}
                        >
                          {request.type?.replace("_", " ") || "Blocked"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            maxWidth: "200px",
                          }}
                        >
                          {request.reason || "-"}
                        </td>
                        <td style={{ padding: "12px" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              justifyContent: "center",
                            }}
                          >
                            <button
                              onClick={() => handleAction(request, "approve")}
                              disabled={processingId === request._id}
                              style={{
                                padding: "6px 12px",
                                background:
                                  processingId === request._id
                                    ? "#d1d5db"
                                    : "#10b981",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "500",
                                cursor:
                                  processingId === request._id
                                    ? "not-allowed"
                                    : "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ✓ Approve
                            </button>
                            <button
                              onClick={() => handleAction(request, "deny")}
                              disabled={processingId === request._id}
                              style={{
                                padding: "6px 12px",
                                background:
                                  processingId === request._id
                                    ? "#d1d5db"
                                    : "#ef4444",
                                color: "white",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: "500",
                                cursor:
                                  processingId === request._id
                                    ? "not-allowed"
                                    : "pointer",
                                whiteSpace: "nowrap",
                              }}
                            >
                              ✕ Deny
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* History Table */}
        {activeTab === "history" && (
          <>
            {processedRequests.length === 0 ? (
              <div
                style={{
                  background: "#f8f9fa",
                  padding: "40px",
                  borderRadius: "8px",
                  textAlign: "center",
                  color: "#6b7280",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>📚</div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}
                >
                  No History Yet
                </div>
                <div style={{ fontSize: "14px" }}>
                  Approved and denied requests will appear here
                </div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    background: "white",
                    borderRadius: "8px",
                    overflow: "hidden",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: "#f8f9fa",
                        borderBottom: "2px solid #e5e7eb",
                      }}
                    >
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Professional
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Start
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        End
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Duration
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Reason
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          fontSize: "13px",
                          color: "#374151",
                        }}
                      >
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {processedRequests.map((request) => (
                      <tr
                        key={request._id}
                        style={{ borderBottom: "1px solid #e5e7eb" }}
                      >
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          <div style={{ fontWeight: "500" }}>
                            {request.staffId?.userId?.firstName || "N/A"}{" "}
                            {request.staffId?.userId?.lastName || ""}
                          </div>
                          <div style={{ fontSize: "12px", color: "#6b7280" }}>
                            Professional
                          </div>
                        </td>
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          {formatDate(request.startDate)}
                        </td>
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          {formatDate(request.endDate)}
                        </td>
                        <td style={{ padding: "12px", fontSize: "14px" }}>
                          {getDuration(request.startDate, request.endDate)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "14px",
                            textTransform: "capitalize",
                          }}
                        >
                          {request.type?.replace("_", " ") || "Blocked"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            maxWidth: "200px",
                          }}
                        >
                          {request.reason || "-"}
                        </td>
                        <td style={{ padding: "12px", textAlign: "center" }}>
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "600",
                              background:
                                request.approvalStatus === "approved"
                                  ? "#d1fae5"
                                  : "#fee2e2",
                              color:
                                request.approvalStatus === "approved"
                                  ? "#065f46"
                                  : "#991b1b",
                            }}
                          >
                            {request.approvalStatus === "approved"
                              ? "✓ Approved"
                              : "✕ Denied"}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          {request.approvedAt
                            ? formatDate(request.approvedAt)
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Approval/Denial Modal */}
        {showModal && selectedRequest && (
          <>
            <div
              onClick={() => setShowModal(false)}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                zIndex: 999,
              }}
            />
            <div
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "white",
                borderRadius: "12px",
                padding: "24px",
                zIndex: 1000,
                maxWidth: "500px",
                width: "90%",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "16px",
                }}
              >
                {modalAction === "approve" ? "✅ Approve" : "❌ Deny"} Time-Off
                Request
              </h2>

              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "14px", marginBottom: "8px" }}>
                  <strong>
                    {selectedRequest.staffId?.userId?.firstName}{" "}
                    {selectedRequest.staffId?.userId?.lastName}
                  </strong>
                </div>
                <div style={{ fontSize: "14px", color: "#6b7280" }}>
                  {formatDate(selectedRequest.startDate)} -{" "}
                  {formatDate(selectedRequest.endDate)}
                </div>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {modalAction === "approve"
                    ? "Approval Notes (Optional)"
                    : "Reason for Denial (Recommended)"}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    modalAction === "approve"
                      ? "Add any notes about this approval..."
                      : "Explain why this request is being denied..."
                  }
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                />
              </div>

              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitAction}
                  disabled={processingId}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: processingId
                      ? "#d1d5db"
                      : modalAction === "approve"
                        ? "#10b981"
                        : "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: processingId ? "not-allowed" : "pointer",
                  }}
                >
                  {processingId
                    ? "Processing..."
                    : modalAction === "approve"
                      ? "Approve Request"
                      : "Deny Request"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </SidebarLayout>
  );
};

export default TimeOffRequests;
